import React from 'react';
import Icon from '../../../components/AppIcon';

const ComparisonChart = () => {
  const comparisonData = [
    {
      category: 'Payroll Processing',
      manual: { time: '10 days', cost: 8500, errors: 45 },
      automated: { time: '2 days', cost: 1200, errors: 2 },
      savings: 86
    },
    {
      category: 'Compliance Monitoring',
      manual: { time: '40 hrs/week', cost: 6200, errors: 28 },
      automated: { time: '5 hrs/week', cost: 950, errors: 1 },
      savings: 85
    },
    {
      category: 'Leave Management',
      manual: { time: '3 days', cost: 3500, errors: 18 },
      automated: { time: '2 hours', cost: 450, errors: 0 },
      savings: 87
    },
    {
      category: 'Document Management',
      manual: { time: '25 hrs/week', cost: 4800, errors: 35 },
      automated: { time: '3 hrs/week', cost: 600, errors: 1 },
      savings: 88
    },
    {
      category: 'Workforce Analytics',
      manual: { time: '5 days', cost: 5200, errors: 22 },
      automated: { time: '15 minutes', cost: 350, errors: 0 },
      savings: 93
    }
  ];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-6">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="BarChart3" size={24} className="text-emerald-600 dark:text-emerald-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Manual vs Automated Comparison</h3>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        See how automation transforms HR operations with dramatic time and cost reductions.
      </p>
      {/* Legend */}
      <div className="flex items-center gap-6 mb-6 pb-4 border-b border-gray-200 dark:border-slate-600">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span className="text-sm text-gray-600 dark:text-gray-400">Manual Process</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-emerald-500 rounded"></div>
          <span className="text-sm text-gray-600 dark:text-gray-400">Automated Process</span>
        </div>
      </div>
      {/* Comparison Items */}
      <div className="space-y-6">
        {comparisonData?.map((item, index) => (
          <div key={index} className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{item?.category}</h4>
              <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1 rounded-full">
                <Icon name="TrendingDown" size={14} className="text-emerald-600 dark:text-emerald-400" />
                <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                  {item?.savings}% savings
                </span>
              </div>
            </div>

            {/* Cost Comparison Bar */}
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-20 text-xs text-gray-500 dark:text-gray-400">Manual</div>
                <div className="flex-1 bg-gray-100 dark:bg-slate-700 rounded-full h-8 relative overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-red-500 flex items-center justify-end px-3 transition-all"
                    style={{ width: '100%' }}
                  >
                    <span className="text-xs font-semibold text-white">K{item?.manual?.cost?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-20 text-xs text-gray-500 dark:text-gray-400">Automated</div>
                <div className="flex-1 bg-gray-100 dark:bg-slate-700 rounded-full h-8 relative overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-emerald-500 flex items-center justify-end px-3 transition-all"
                    style={{ width: `${(item?.automated?.cost / item?.manual?.cost) * 100}%` }}
                  >
                    <span className="text-xs font-semibold text-white">K{item?.automated?.cost?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Time</div>
                <div className="text-sm font-semibold text-red-600 dark:text-red-400 line-through">
                  {item?.manual?.time}
                </div>
                <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                  {item?.automated?.time}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Cost/Month</div>
                <div className="text-sm font-semibold text-red-600 dark:text-red-400 line-through">
                  K{item?.manual?.cost?.toLocaleString()}
                </div>
                <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                  K{item?.automated?.cost?.toLocaleString()}
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-lg p-3">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Errors</div>
                <div className="text-sm font-semibold text-red-600 dark:text-red-400 line-through">
                  {item?.manual?.errors}
                </div>
                <div className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                  {item?.automated?.errors}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Summary */}
      <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-600">
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Average Cost Reduction</div>
              <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">88%</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Average Time Savings</div>
              <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">85%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonChart;